import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaEdit, 
  FaCog, 
  FaBookmark, 
  FaHeart, 
  FaImages, 
  FaSignOutAlt,
  FaBriefcase,
  FaMapMarkerAlt,
  FaWallet,
  FaCreditCard
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ProfileSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background-color: #111;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #333;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #333;
`;

const HeaderTitle = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SettingsButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #333;
  }
`;

const ProfileInfo = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const ProfileImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  margin: 0 auto 20px;
  object-fit: cover;
  border: 3px solid #4CAF50;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const UserName = styled.h2`
  color: #fff;
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const UserEmail = styled.p`
  color: #ccc;
  font-size: 16px;
  margin-bottom: 16px;
`;

const UserBio = styled.p`
  color: #ccc;
  font-size: 16px;
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto 24px;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 32px;
  padding: 24px 0;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  color: #ccc;
  font-size: 14px;
`;

const AdditionalInfo = styled.div`
  margin-bottom: 32px;
  text-align: center;
`;

const AdditionalInfoText = styled.div`
  color: #ccc;
  font-size: 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const EditButton = styled.button`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 32px;

  &:hover {
    background-color: #45a049;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 24px 0;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
  margin-bottom: 32px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #fff;
    background-color: #333;
  }

  svg {
    font-size: 20px;
  }
`;

const ActionButtonText = styled.span`
  font-size: 14px;
`;

const PostsSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
`;

const EmptyPosts = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyPostsIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #666;
`;

const EmptyPostsText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

const EmptyPostsSubtext = styled.div`
  font-size: 16px;
  color: #ccc;
`;

const LogoutButton = styled.button`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;

  &:hover {
    background-color: #45a049;
  }
`;

const ProfileScreen = () => {
  const { user, logout, refreshUserData } = useAuth();
  const [refreshing, setRefreshing] = useState(false);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      toast.success('Logged out successfully');
    }
  };

  const handleEditProfile = () => {
    toast('Edit profile functionality coming soon!');
  };

  const handleSettings = () => {
    toast('Settings functionality coming soon!');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshUserData();
      toast.success('Profile refreshed!');
    } catch (error) {
      toast.error('Failed to refresh profile');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Container>
      <ProfileSection>
        <Header>
          <HeaderTitle>Profile</HeaderTitle>
          <SettingsButton onClick={handleSettings}>
            <FaCog />
          </SettingsButton>
        </Header>

        <ProfileInfo>
          <ProfileImage
            src={user?.meta?.profile_image || '/default-avatar.png'}
            alt="Profile"
          />
          <UserName>
            {user?.meta?.name || user?.username || 'User Name'}
          </UserName>
          <UserEmail>{user?.email || 'user@example.com'}</UserEmail>
          
          {user?.meta?.bio && <UserBio>{user.meta.bio}</UserBio>}
          
          <StatsContainer>
            <StatItem>
              <StatNumber>{user?.stats?.posts || 0}</StatNumber>
              <StatLabel>Posts</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{user?.stats?.followers || 0}</StatNumber>
              <StatLabel>Followers</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{user?.stats?.followings || 0}</StatNumber>
              <StatLabel>Following</StatLabel>
            </StatItem>
          </StatsContainer>

          <AdditionalInfo>
            {user?.meta?.occupation && (
              <AdditionalInfoText>
                <FaBriefcase />
                {user.meta.occupation}
              </AdditionalInfoText>
            )}
            {user?.meta?.location && (
              <AdditionalInfoText>
                <FaMapMarkerAlt />
                {user.meta.location}
              </AdditionalInfoText>
            )}
            {user?.stats?.wallet && (
              <AdditionalInfoText>
                <FaWallet />
                Wallet: {user.stats.wallet}
              </AdditionalInfoText>
            )}
            {user?.stats?.balance && (
              <AdditionalInfoText>
                <FaCreditCard />
                Balance: {user.stats.balance}
              </AdditionalInfoText>
            )}
          </AdditionalInfo>

          <EditButton onClick={handleEditProfile}>
            <FaEdit style={{ marginRight: 8 }} />
            Edit Profile
          </EditButton>
        </ProfileInfo>

        <ActionButtons>
          <ActionButton>
            <FaBookmark />
            <ActionButtonText>Saved</ActionButtonText>
          </ActionButton>
          <ActionButton>
            <FaHeart />
            <ActionButtonText>Liked</ActionButtonText>
          </ActionButton>
        </ActionButtons>

        <PostsSection>
          <SectionTitle>Posts</SectionTitle>
          <EmptyPosts>
            <EmptyPostsIcon>
              <FaImages />
            </EmptyPostsIcon>
            <EmptyPostsText>No posts yet</EmptyPostsText>
            <EmptyPostsSubtext>Share your first post!</EmptyPostsSubtext>
          </EmptyPosts>
        </PostsSection>

        <LogoutButton onClick={handleLogout}>
          <FaSignOutAlt />
          Logout
        </LogoutButton>
      </ProfileSection>
    </Container>
  );
};

export default ProfileScreen; 